import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { coursesApi, courseTestsApi, testBankApi, instrumentsApi } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { ArrowLeft, Plus, Trash, LinkSimple, VideoCamera, FilePdf, File, ClipboardText, MagnifyingGlass, ListChecks } from '@phosphor-icons/react';
import { toast } from 'sonner';

const MATERIAL_TYPES = [
  { value: 'pdf', label: 'PDF', icon: FilePdf },
  { value: 'video', label: 'Video URL', icon: VideoCamera },
  { value: 'link', label: 'Enlace', icon: LinkSimple },
  { value: 'document', label: 'Documento', icon: File },
];

export default function CourseConfigPage() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('materials');

  // Materials state
  const [materials, setMaterials] = useState([]);
  const [showAddMaterial, setShowAddMaterial] = useState(false);
  const [materialForm, setMaterialForm] = useState({ name: '', material_type: 'link', url: '' });

  // Tests assignment state
  const [assignedTests, setAssignedTests] = useState([]);
  const [showTestSelector, setShowTestSelector] = useState(false);
  const [allTests, setAllTests] = useState([]);
  const [testSearch, setTestSearch] = useState('');

  // Instruments state
  const [instruments, setInstruments] = useState([]);
  const [showAddInstrument, setShowAddInstrument] = useState(false);
  const [instrumentForm, setInstrumentForm] = useState({
    name: '', instrument_type: 'checklist', description: '',
    criteria: [{ name: '', description: '', max_score: 1 }]
  });

  const fetchCourse = useCallback(async () => {
    try {
      const res = await coursesApi.getById(courseId);
      setCourse(res.data);
    } catch {
      toast.error('Curso no encontrado');
      navigate('/admin/courses');
    } finally {
      setLoading(false);
    }
  }, [courseId, navigate]);

  const fetchMaterials = useCallback(async () => {
    try {
      const res = await coursesApi.getMaterials(courseId);
      setMaterials(res.data);
    } catch { /* ignore */ }
  }, [courseId]);

  const fetchAssignedTests = useCallback(async () => {
    try {
      const res = await courseTestsApi.getAssignedTests(courseId);
      setAssignedTests(res.data);
    } catch { /* ignore */ }
  }, [courseId]);

  const fetchAllTests = useCallback(async () => {
    try {
      const res = await testBankApi.getAll();
      setAllTests(res.data);
    } catch { /* ignore */ }
  }, []);

  const fetchInstruments = useCallback(async () => {
    try {
      const res = await instrumentsApi.list(courseId);
      setInstruments(res.data);
    } catch { /* ignore */ }
  }, [courseId]);

  useEffect(() => {
    fetchCourse();
    fetchMaterials();
    fetchAssignedTests();
    fetchAllTests();
    fetchInstruments();
  }, [fetchCourse, fetchMaterials, fetchAssignedTests, fetchAllTests, fetchInstruments]);

  // Materials handlers
  const handleAddMaterial = async (e) => {
    e.preventDefault();
    try {
      await coursesApi.addMaterial(courseId, materialForm);
      toast.success('Material agregado');
      setMaterialForm({ name: '', material_type: 'link', url: '' });
      setShowAddMaterial(false);
      fetchMaterials();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al agregar material');
    }
  };

  const handleDeleteMaterial = async (materialId) => {
    try {
      await coursesApi.deleteMaterial(courseId, materialId);
      toast.success('Material eliminado');
      fetchMaterials();
    } catch { toast.error('Error al eliminar'); }
  };

  // Test assignment handlers
  const handleAssignTest = async (testBankId) => {
    try {
      await courseTestsApi.assignTest(courseId, testBankId);
      toast.success('Test asignado al curso');
      fetchAssignedTests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al asignar');
    }
  };

  const handleUnassignTest = async (testBankId) => {
    try {
      await courseTestsApi.unassignTest(courseId, testBankId);
      toast.success('Test desvinculado del curso');
      fetchAssignedTests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al desvincular');
    }
  };

  // Instrument handlers
  const handleAddInstrument = async () => {
    if (!instrumentForm.name.trim() || instrumentForm.criteria.length === 0) {
      toast.error('Ingresa nombre y al menos un criterio'); return;
    }
    const validCriteria = instrumentForm.criteria.filter(c => c.name.trim());
    if (validCriteria.length === 0) { toast.error('Al menos un criterio debe tener nombre'); return; }
    try {
      await instrumentsApi.create({
        course_id: courseId,
        name: instrumentForm.name,
        instrument_type: instrumentForm.instrument_type,
        description: instrumentForm.description,
        criteria: validCriteria.map((c, i) => ({ ...c, order: i })),
      });
      toast.success('Instrumento creado');
      setShowAddInstrument(false);
      setInstrumentForm({ name: '', instrument_type: 'checklist', description: '', criteria: [{ name: '', description: '', max_score: 1 }] });
      fetchInstruments();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al crear'); }
  };

  const handleDeleteInstrument = async (id) => {
    try {
      await instrumentsApi.delete(id);
      toast.success('Instrumento eliminado');
      fetchInstruments();
    } catch { toast.error('Error al eliminar'); }
  };

  const addCriterion = () => {
    setInstrumentForm(prev => ({
      ...prev,
      criteria: [...prev.criteria, { name: '', description: '', max_score: instrumentForm.instrument_type === 'rubric' ? 5 : 1 }]
    }));
  };

  const removeCriterion = (idx) => {
    setInstrumentForm(prev => ({
      ...prev,
      criteria: prev.criteria.filter((_, i) => i !== idx)
    }));
  };

  const updateCriterion = (idx, field, value) => {
    setInstrumentForm(prev => ({
      ...prev,
      criteria: prev.criteria.map((c, i) => i === idx ? { ...c, [field]: value } : c)
    }));
  };

  const getTypeIcon = (type) => {
    const t = MATERIAL_TYPES.find(m => m.value === type);
    const Icon = t ? t.icon : File;
    return <Icon size={18} className="text-[#2B4B8C]/60" />;
  };

  if (loading || !course) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2BBAA4]" /></div>;
  }

  const assignedIds = assignedTests.map(t => t.id);
  const availableTests = allTests.filter(t =>
    t.is_active && !assignedIds.includes(t.id) && t.course_id === courseId &&
    (testSearch === '' || t.name.toLowerCase().includes(testSearch.toLowerCase()))
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="course-config-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/courses')} className="text-[#2B4B8C]/60 hover:text-[#2B4B8C] -ml-2" data-testid="back-to-courses">
          <ArrowLeft size={20} className="mr-1" /> Volver
        </Button>
      </div>

      <div>
        <h1 className="text-2xl font-bold text-[#2B4B8C]">Configurar: {course.name}</h1>
        <p className="text-[#2B4B8C]/50 text-sm">{course.training_type} &middot; {course.default_duration_minutes} min &middot; {course.default_passing_percentage}% aprobación</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[#f8fafc] border border-[#2B4B8C]/10">
          <TabsTrigger value="materials" className="data-[state=active]:bg-white data-[state=active]:text-[#2B4B8C]" data-testid="tab-materials">
            <LinkSimple size={16} className="mr-1.5" /> Material de Apoyo ({materials.length})
          </TabsTrigger>
          <TabsTrigger value="tests" className="data-[state=active]:bg-white data-[state=active]:text-[#2B4B8C]" data-testid="tab-tests">
            <ClipboardText size={16} className="mr-1.5" /> Tests Asignados ({assignedTests.length})
          </TabsTrigger>
          <TabsTrigger value="instruments" className="data-[state=active]:bg-white data-[state=active]:text-[#2B4B8C]" data-testid="tab-instruments">
            <ListChecks size={16} className="mr-1.5" /> Instrumentos ({instruments.length})
          </TabsTrigger>
        </TabsList>

        {/* Materials Tab */}
        <TabsContent value="materials" className="space-y-4 mt-4">
          <div className="flex justify-end">
            <Button onClick={() => setShowAddMaterial(!showAddMaterial)} className="bg-[#2BBAA4] text-black hover:bg-[#239986]" data-testid="add-material-btn">
              <Plus size={18} className="mr-2" /> Agregar Material
            </Button>
          </div>

          {showAddMaterial && (
            <Card className="bg-[#f8fafc] border-[#2B4B8C]/10">
              <CardContent className="p-5">
                <form onSubmit={handleAddMaterial} className="space-y-4" data-testid="add-material-form">
                  <p className="font-medium text-[#2B4B8C]">Nuevo Material</p>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-1">
                      <Label className="text-xs text-[#2B4B8C]/70">Nombre *</Label>
                      <Input value={materialForm.name} onChange={(e) => setMaterialForm({ ...materialForm, name: e.target.value })} className="bg-white border-[#2B4B8C]/10 text-[#2B4B8C]" required data-testid="material-name" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-[#2B4B8C]/70">Tipo</Label>
                      <Select value={materialForm.material_type} onValueChange={(v) => setMaterialForm({ ...materialForm, material_type: v })}>
                        <SelectTrigger className="bg-white border-[#2B4B8C]/10 text-[#2B4B8C]" data-testid="material-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-[#2B4B8C]/10">
                          {MATERIAL_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-[#2B4B8C]/70">URL *</Label>
                      <Input value={materialForm.url} onChange={(e) => setMaterialForm({ ...materialForm, url: e.target.value })} className="bg-white border-[#2B4B8C]/10 text-[#2B4B8C]" placeholder="https://..." required data-testid="material-url" />
                    </div>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <Button type="button" variant="outline" onClick={() => setShowAddMaterial(false)} className="border-[#2B4B8C]/10">Cancelar</Button>
                    <Button type="submit" className="bg-[#2BBAA4] text-black hover:bg-[#239986]" data-testid="submit-material">Guardar</Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          <Card className="bg-white border-[#2B4B8C]/10">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#2B4B8C]/10 hover:bg-transparent">
                    <TableHead className="text-[#2B4B8C]/60">Tipo</TableHead>
                    <TableHead className="text-[#2B4B8C]/60">Nombre</TableHead>
                    <TableHead className="text-[#2B4B8C]/60">URL</TableHead>
                    <TableHead className="text-[#2B4B8C]/60 text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {materials.map((m) => (
                    <TableRow key={m.id} className="border-[#2B4B8C]/5 hover:bg-[#f8fafc]">
                      <TableCell>{getTypeIcon(m.material_type)}</TableCell>
                      <TableCell className="text-[#2B4B8C] font-medium">{m.name}</TableCell>
                      <TableCell>
                        <a href={m.url} target="_blank" rel="noopener noreferrer" className="text-[#2BBAA4] hover:underline text-sm truncate block max-w-xs">{m.url}</a>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteMaterial(m.id)} className="text-red-400 hover:text-red-500" data-testid={`delete-material-${m.id}`}>
                          <Trash size={16} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {materials.length === 0 && <div className="text-center py-12 text-[#2B4B8C]/40">No hay materiales de apoyo</div>}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tests Tab */}
        <TabsContent value="tests" className="space-y-4 mt-4">
          <div className="flex justify-end">
            <Button onClick={() => setShowTestSelector(!showTestSelector)} className="bg-[#2BBAA4] text-black hover:bg-[#239986]" data-testid="assign-test-btn">
              <Plus size={18} className="mr-2" /> Asignar Test
            </Button>
          </div>

          {showTestSelector && (
            <Card className="bg-[#f8fafc] border-[#2B4B8C]/10">
              <CardContent className="p-5 space-y-3">
                <p className="font-medium text-[#2B4B8C]">Seleccionar Test del Banco</p>
                <div className="relative">
                  <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#2B4B8C]/60" size={18} />
                  <Input
                    placeholder="Buscar test..."
                    value={testSearch}
                    onChange={(e) => setTestSearch(e.target.value)}
                    className="pl-10 bg-white border-[#2B4B8C]/10 text-[#2B4B8C]"
                    data-testid="search-tests-to-assign"
                  />
                </div>
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {availableTests.length === 0 ? (
                    <p className="text-center py-4 text-[#2B4B8C]/40 text-sm">
                      {allTests.length === 0 ? 'No hay tests en el banco. Cree tests primero.' : 'No hay tests disponibles para asignar.'}
                    </p>
                  ) : (
                    availableTests.map((test) => (
                      <div key={test.id} className="flex items-center justify-between p-3 rounded-lg bg-white border border-[#2B4B8C]/10">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <ClipboardText size={16} className="text-[#2BBAA4] shrink-0" />
                            <span className="text-sm font-medium text-[#2B4B8C]">{test.name}</span>
                          </div>
                          <div className="flex gap-3 text-xs text-[#2B4B8C]/50 ml-6 mt-0.5">
                            <span>{test.time_limit_minutes} min</span>
                            <span>{test.passing_percentage}%</span>
                            <span>{test.questions_count} preguntas</span>
                          </div>
                        </div>
                        <Button size="sm" onClick={() => handleAssignTest(test.id)} className="bg-[#2BBAA4] text-black hover:bg-[#239986] h-7 text-xs shrink-0" data-testid={`assign-test-${test.id}`}>
                          <Plus size={14} className="mr-1" /> Asignar
                        </Button>
                      </div>
                    ))
                  )}
                </div>
                <Button type="button" variant="outline" onClick={() => setShowTestSelector(false)} className="border-[#2B4B8C]/10">Cerrar</Button>
              </CardContent>
            </Card>
          )}

          {/* Assigned Tests */}
          <div className="space-y-3">
            {assignedTests.map((test) => (
              <Card key={test.id} className="bg-white border-[#2B4B8C]/10" data-testid={`assigned-test-${test.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <ClipboardText size={20} className="text-[#2B4B8C]" />
                        <h3 className="font-medium text-[#2B4B8C]">{test.name}</h3>
                      </div>
                      {test.description && <p className="text-xs text-[#2B4B8C]/50 ml-7">{test.description}</p>}
                      <div className="flex gap-4 text-xs text-[#2B4B8C]/60 ml-7 mt-1">
                        <span>{test.time_limit_minutes} min</span>
                        <span>{test.passing_percentage}% aprobación</span>
                        <span>{test.max_attempts} intentos</span>
                        <span className="text-[#2BBAA4] font-medium">{test.questions_count} preguntas</span>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => handleUnassignTest(test.id)} className="text-red-400 hover:text-red-500" data-testid={`unassign-test-${test.id}`}>
                      <Trash size={16} />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            {assignedTests.length === 0 && (
              <Card className="bg-white border-[#2B4B8C]/10">
                <CardContent className="text-center py-12 text-[#2B4B8C]/40">
                  No hay tests asignados a este curso. Asigne tests del banco para habilitar evaluaciones.
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Instruments Tab */}
        <TabsContent value="instruments" className="space-y-4 mt-4">
          <div className="flex justify-end">
            <Button onClick={() => setShowAddInstrument(!showAddInstrument)} className="bg-[#2BBAA4] text-black hover:bg-[#239986]" data-testid="add-instrument-btn">
              <Plus size={18} className="mr-2" /> Crear Instrumento
            </Button>
          </div>

          {showAddInstrument && (
            <Card className="bg-[#f8fafc] border-[#2B4B8C]/10">
              <CardContent className="p-5 space-y-4">
                <p className="font-medium text-[#2B4B8C]">Nuevo Instrumento de Evaluacion</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs text-[#2B4B8C]/70">Nombre *</Label>
                    <Input value={instrumentForm.name} onChange={(e) => setInstrumentForm(p => ({ ...p, name: e.target.value }))} className="bg-white border-[#2B4B8C]/10 text-[#2B4B8C]" data-testid="instrument-name" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-[#2B4B8C]/70">Tipo</Label>
                    <Select value={instrumentForm.instrument_type} onValueChange={(v) => setInstrumentForm(p => ({ ...p, instrument_type: v }))}>
                      <SelectTrigger className="bg-white border-[#2B4B8C]/10 text-[#2B4B8C]" data-testid="instrument-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-[#2B4B8C]/10">
                        <SelectItem value="checklist">Tabla de Cotejo</SelectItem>
                        <SelectItem value="rubric">Rubrica</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-[#2B4B8C]/70">Descripcion</Label>
                  <Input value={instrumentForm.description} onChange={(e) => setInstrumentForm(p => ({ ...p, description: e.target.value }))} className="bg-white border-[#2B4B8C]/10 text-[#2B4B8C]" />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-[#2B4B8C]/70 font-medium">Criterios</Label>
                    <Button type="button" size="sm" variant="outline" onClick={addCriterion} className="h-7 text-xs border-[#2B4B8C]/10" data-testid="add-criterion-btn">
                      <Plus size={12} className="mr-1" /> Agregar
                    </Button>
                  </div>
                  {instrumentForm.criteria.map((c, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 rounded bg-white border border-[#2B4B8C]/10">
                      <span className="text-xs text-[#2B4B8C]/40 w-6 text-center">{idx + 1}</span>
                      <Input
                        placeholder="Nombre del criterio"
                        value={c.name}
                        onChange={(e) => updateCriterion(idx, 'name', e.target.value)}
                        className="flex-1 h-8 text-sm border-[#2B4B8C]/10"
                        data-testid={`criterion-name-${idx}`}
                      />
                      {instrumentForm.instrument_type === 'rubric' && (
                        <Input
                          type="number" min="1" max="10" step="1"
                          value={c.max_score}
                          onChange={(e) => updateCriterion(idx, 'max_score', parseFloat(e.target.value) || 1)}
                          className="w-20 h-8 text-sm text-center border-[#2B4B8C]/10"
                          placeholder="Max"
                          data-testid={`criterion-max-${idx}`}
                        />
                      )}
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeCriterion(idx)} className="h-8 w-8 p-0 text-red-400 hover:text-red-500" disabled={instrumentForm.criteria.length <= 1}>
                        <Trash size={14} />
                      </Button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 pt-1">
                  <Button type="button" variant="outline" onClick={() => setShowAddInstrument(false)} className="border-[#2B4B8C]/10">Cancelar</Button>
                  <Button onClick={handleAddInstrument} className="bg-[#2BBAA4] text-black hover:bg-[#239986]" data-testid="submit-instrument">Crear Instrumento</Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Existing Instruments */}
          <div className="space-y-3">
            {instruments.map((inst) => (
              <Card key={inst.id} className="bg-white border-[#2B4B8C]/10" data-testid={`instrument-${inst.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <ListChecks size={20} className="text-[#2B4B8C]" />
                        <h3 className="font-medium text-[#2B4B8C]">{inst.name}</h3>
                        <Badge variant="outline" className={inst.instrument_type === 'checklist' ? 'bg-blue-500/10 text-blue-600 border-blue-500/20' : 'bg-purple-500/10 text-purple-600 border-purple-500/20'}>
                          {inst.instrument_type === 'checklist' ? 'Tabla de Cotejo' : 'Rubrica'}
                        </Badge>
                      </div>
                      {inst.description && <p className="text-xs text-[#2B4B8C]/50 ml-7">{inst.description}</p>}
                      <div className="ml-7 mt-2 space-y-1">
                        {inst.criteria.map((c, i) => (
                          <div key={c.id} className="flex items-center gap-2 text-xs text-[#2B4B8C]/60">
                            <span className="w-5 h-5 rounded bg-[#2B4B8C]/5 flex items-center justify-center text-[10px] font-medium">{i + 1}</span>
                            <span>{c.name}</span>
                            {inst.instrument_type === 'rubric' && <span className="text-[#2BBAA4]">(max: {c.max_score})</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteInstrument(inst.id)} className="text-red-400 hover:text-red-500" data-testid={`delete-instrument-${inst.id}`}>
                      <Trash size={16} />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            {instruments.length === 0 && (
              <Card className="bg-white border-[#2B4B8C]/10">
                <CardContent className="text-center py-12 text-[#2B4B8C]/40">
                  <ListChecks size={40} className="mx-auto mb-3 opacity-30" />
                  <p className="text-sm">No hay instrumentos de evaluacion</p>
                  <p className="text-xs mt-1">Cree tablas de cotejo o rubricas para que el relator pueda evaluar trabajadores</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
